/**
 * Power-unit picker for standalone belt grinder housings (BG10/BG20/BG30).
 *
 * The section (sections/power-unit-picker.liquid) renders one
 * #PowerUnitPicker-<id> mount point per product page, with data attributes
 * carrying: the housing's own variant ID (this product's single real
 * variant), and a JSON list of top-level power options configured via
 * theme editor blocks -- "Housing only" (always first, no motor), a flat
 * "Pneumatic" option (a direct motor variant), and/or an "Electric"
 * voltage option (240V/110V) whose `voltage` field is set instead of a
 * fixed motorVariantId.
 *
 * Electric options are a two-step pick: choosing a voltage doesn't add
 * anything by itself -- it reveals a second row of brand/type choices
 * (Green 800W Fixed Speed, Bosch 900W Variable Speed, Metabo 1100W
 * Variable Speed), each a real, separately-priced Motor Unit product
 * variant at that voltage (see ELECTRIC_BRAND_OPTIONS below). This mirrors
 * the same choice offered on the Beltafile housing inside the Build Your
 * Belt-It wizard (assets/beltit-configurator.js) -- both pull from the
 * same three shared motor products, so a price change to one of those
 * products is reflected everywhere it's offered.
 *
 * Cart-add mirrors assets/beltit-configurator.js: a single /cart/add.js
 * call carrying both line items (housing + motor, when a motor is fully
 * selected), requesting cart-icon-bubble/cart-drawer sections back and
 * patching them into the DOM, and treating a non-2xx or Shopify
 * error-shaped response as a real failure rather than a silent "success"
 * (see that file's header for why this check matters).
 */

(function () {
  'use strict';

  // Real Shopify variant IDs for the three motor brands/types, at each
  // voltage. Shared across every product that offers an "Electric" choice
  // with a brand pick (currently: this picker's BG20/BG30, and the
  // Beltafile housing inside the Build Your Belt-It wizard). Kept here
  // rather than in theme-editor block settings because these three brands
  // and their variant IDs never vary by housing -- only which voltages a
  // given housing offers at all does, which the section still configures
  // via blocks.
  var ELECTRIC_BRAND_OPTIONS = {
    '240V': [
      { label: '800W Fixed Speed', variantId: '62522681491843', price: 150 },
      { label: 'Bosch 900W Variable Speed', variantId: '62522681557379', price: 240 },
      { label: 'Metabo 1100W Variable Speed', variantId: '62522681655683', price: 260 }
    ],
    '110V': [
      { label: '800W Fixed Speed', variantId: '62522681524611', price: 150 },
      { label: 'Bosch 900W Variable Speed', variantId: '62522681590147', price: 240 },
      { label: 'Metabo 1100W Variable Speed', variantId: '62522681688451', price: 260 }
    ]
  };

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.textContent = str == null ? '' : String(str);
    return div.innerHTML;
  }

  function formatPrice(amount) {
    return '£' + Number(amount).toFixed(2);
  }

  function updateCartUI(data) {
    if (!data || !data.sections) return;
    var cartDrawer = document.querySelector('cart-drawer');

    if (cartDrawer && typeof cartDrawer.getSectionsToRender === 'function') {
      cartDrawer.getSectionsToRender().forEach(function (section) {
        var html = data.sections[section.id];
        if (!html) return;
        var sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        if (!sectionElement) return;
        sectionElement.innerHTML = cartDrawer.getSectionInnerHTML(html, section.selector);
      });
      var inner = cartDrawer.querySelector('.drawer__inner');
      if (inner) inner.classList.remove('is-empty');
      return;
    }

    var bubbleEl = document.getElementById('cart-icon-bubble');
    var bubbleHtml = data.sections['cart-icon-bubble'];
    if (bubbleEl && bubbleHtml) {
      var wrapper = new DOMParser().parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
      if (wrapper) bubbleEl.innerHTML = wrapper.innerHTML;
    }
  }

  function addToCart(items) {
    var cartDrawer = document.querySelector('cart-drawer');
    var sections =
      cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.id; })
        : ['cart-icon-bubble'];

    var payload = {
      items: items,
      sections: sections,
      sections_url: window.location.pathname
    };

    return fetch((window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add') + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok || (data && data.status)) {
            var message = (data && (data.description || data.message)) ||
              'Unable to add this to your basket. Please try again in a moment.';
            var error = new Error(message);
            error.isCartError = true;
            throw error;
          }
          return data;
        });
      })
      .then(function (data) {
        updateCartUI(data);
        return data;
      });
  }

  function initPicker(root) {
    var housingVariantId = root.getAttribute('data-housing-variant-id');
    var options;
    try {
      options = JSON.parse(root.getAttribute('data-options') || '[]');
    } catch (e) {
      options = [];
    }

    var selectedIndex = null; // index into `options`
    var selectedBrandIndex = null; // index into ELECTRIC_BRAND_OPTIONS[voltage], only when the selected option has a voltage
    var isAdding = false;
    var addedState = false;

    function selectedOption() {
      return selectedIndex === null ? null : options[selectedIndex];
    }

    function resolvedMotor() {
      var option = selectedOption();
      if (!option) return null;
      if (option.voltage) {
        if (selectedBrandIndex === null) return null;
        return ELECTRIC_BRAND_OPTIONS[option.voltage][selectedBrandIndex];
      }
      return option.motorVariantId ? { variantId: option.motorVariantId, price: option.price } : null;
    }

    function canAdd() {
      var option = selectedOption();
      if (!option) return false;
      if (option.voltage) return selectedBrandIndex !== null;
      return true;
    }

    function render() {
      if (addedState) {
        root.innerHTML =
          '<div class="power-unit-picker__confirmation">' +
          '<div class="power-unit-picker__confirmation-icon">✓</div>' +
          '<p class="power-unit-picker__confirmation-text">Added to your basket.</p>' +
          '<div class="power-unit-picker__actions">' +
          '<a href="/cart" class="button">View cart</a>' +
          '<button type="button" class="button button--secondary" data-pick-again>Choose another option</button>' +
          '</div>' +
          '</div>';
        return;
      }

      var cards = options.map(function (option, index) {
        var selected = index === selectedIndex;
        return (
          '<button type="button" class="power-unit-picker__option' + (selected ? ' is-selected' : '') + '" data-option-index="' + index + '">' +
          (selected ? '<span class="power-unit-picker__check">✓</span>' : '') +
          '<span class="power-unit-picker__option-label">' + escapeHtml(option.label) + '</span>' +
          (option.priceNote ? '<span class="power-unit-picker__option-price">' + escapeHtml(option.priceNote) + '</span>' : '') +
          '</button>'
        );
      }).join('');

      var option = selectedOption();
      var brandCards = '';
      if (option && option.voltage) {
        var brands = ELECTRIC_BRAND_OPTIONS[option.voltage];
        brandCards =
          '<div class="power-unit-picker__brand-intro">Choose your motor for ' + escapeHtml(option.label) + ':</div>' +
          '<div class="power-unit-picker__options power-unit-picker__options--brands">' +
          brands.map(function (brand, index) {
            var selected = index === selectedBrandIndex;
            return (
              '<button type="button" class="power-unit-picker__option' + (selected ? ' is-selected' : '') + '" data-brand-index="' + index + '">' +
              (selected ? '<span class="power-unit-picker__check">✓</span>' : '') +
              '<span class="power-unit-picker__option-label">' + escapeHtml(brand.label) + '</span>' +
              '<span class="power-unit-picker__option-price">' + formatPrice(brand.price) + '</span>' +
              '</button>'
            );
          }).join('') +
          '</div>';
      }

      root.innerHTML =
        '<div class="power-unit-picker__options">' + cards + '</div>' +
        brandCards +
        '<button type="button" class="button power-unit-picker__add"' + (canAdd() ? '' : ' disabled') + ' data-add-to-basket>' +
        (isAdding ? 'Adding…' : 'Add to Cart') +
        '</button>';
    }

    root.addEventListener('click', function (event) {
      var optionEl = event.target.closest('[data-option-index]');
      if (optionEl) {
        selectedIndex = parseInt(optionEl.getAttribute('data-option-index'), 10);
        selectedBrandIndex = null;
        render();
        return;
      }

      var brandEl = event.target.closest('[data-brand-index]');
      if (brandEl) {
        selectedBrandIndex = parseInt(brandEl.getAttribute('data-brand-index'), 10);
        render();
        return;
      }

      var pickAgainEl = event.target.closest('[data-pick-again]');
      if (pickAgainEl) {
        addedState = false;
        selectedIndex = null;
        selectedBrandIndex = null;
        render();
        return;
      }

      var addEl = event.target.closest('[data-add-to-basket]');
      if (addEl) {
        if (addEl.disabled || isAdding || !canAdd()) return;
        var motor = resolvedMotor();
        var items = [{ id: parseInt(housingVariantId, 10), quantity: 1 }];
        if (motor) {
          items.push({ id: parseInt(motor.variantId, 10), quantity: 1 });
        }

        isAdding = true;
        render();

        addToCart(items)
          .then(function () {
            isAdding = false;
            addedState = true;
            render();
          })
          .catch(function (error) {
            isAdding = false;
            render();
            var message = (error && error.isCartError && error.message) ||
              'Something went wrong adding this to your basket. Please try again.';
            window.alert(message);
          });
        return;
      }
    });

    render();
  }

  document.addEventListener('DOMContentLoaded', function () {
    var pickers = document.querySelectorAll('.power-unit-picker');
    pickers.forEach(function (root) {
      initPicker(root);
    });
  });
})();
